import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import DeleteConfirmModal from './DeleteConfirmModal';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiPlus, FiCalendar, FiPercent, FiCheck, FiEdit, FiArchive, FiRefreshCw, FiSave, FiX, FiEye, FiEyeOff } = FiIcons;

const StrategicGoals = ({ memberId }) => {
  const { getMemberGoals, addMemberGoal, updateMemberGoal, archiveMemberGoal, restoreMemberGoal } = useData();
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [goalToAction, setGoalToAction] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    achieve_by: '',
    percentage_achieved: 0
  });

  const memberGoals = getMemberGoals(memberId);
  const activeGoals = memberGoals.filter(goal => !goal.is_archived);
  const archivedGoals = memberGoals.filter(goal => goal.is_archived);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const goalData = {
        ...formData,
        member_id: memberId,
        coach_id: user.id,
        coach_name: user.name,
        percentage_achieved: Math.min(100, Math.max(0, parseInt(formData.percentage_achieved) || 0))
      };

      if (editingGoal) {
        await updateMemberGoal({
          ...goalData,
          id: editingGoal.id
        });
      } else {
        await addMemberGoal(goalData);
      }

      // Reset form
      setFormData({
        title: '',
        description: '',
        achieve_by: '',
        percentage_achieved: 0
      });
      setShowAddForm(false);
      setEditingGoal(null);
    } catch (error) {
      console.error('Error saving goal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditClick = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title || '',
      description: goal.description || '',
      achieve_by: goal.achieve_by ? new Date(goal.achieve_by).toISOString().split('T')[0] : '',
      percentage_achieved: goal.percentage_achieved || 0
    });
    setShowAddForm(true);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingGoal(null);
    setFormData({
      title: '',
      description: '',
      achieve_by: '',
      percentage_achieved: 0
    });
  };

  const handleArchiveClick = (goal) => {
    setGoalToAction(goal);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (goalToAction) {
      try {
        await archiveMemberGoal(goalToAction.id);
      } catch (error) {
        console.error('Error archiving goal:', error);
      } finally {
        setArchiveModalOpen(false);
        setGoalToAction(null);
      }
    }
  };

  const handleRestoreClick = (goal) => {
    setGoalToAction(goal);
    setRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (goalToAction) {
      try {
        await restoreMemberGoal(goalToAction.id);
      } catch (error) {
        console.error('Error restoring goal:', error);
      } finally {
        setRestoreModalOpen(false);
        setGoalToAction(null);
      }
    }
  };

  const isOverdue = (achieveBy) => {
    if (!achieveBy) return false;
    return new Date(achieveBy) < new Date() && new Date(achieveBy).toDateString() !== new Date().toDateString();
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiTarget} className="w-5 h-5 text-primary-600" />
            <h3 className="text-xl font-semibold text-gray-900">Strategic Goals</h3>
            {archivedGoals.length > 0 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                {archivedGoals.length} archived
              </span>
            )}
          </div>
          {/* SMART Goals Acronym */}
          <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600">
            <span className="font-medium text-primary-700">
              <span className="font-bold text-primary-800">S</span>pecific,{' '}
              <span className="font-bold text-primary-800">M</span>easurable,{' '}
              <span className="font-bold text-primary-800">A</span>chievable,{' '}
              <span className="font-bold text-primary-800">R</span>elevant,{' '}
              <span className="font-bold text-primary-800">T</span>imeframed
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {archivedGoals.length > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={showArchived ? FiEyeOff : FiEye} className="w-4 h-4" />
              <span>{showArchived ? 'Hide' : 'Show'} Archived</span>
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={showAddForm ? FiX : FiPlus} className="w-4 h-4" />
            <span>{showAddForm ? 'Cancel' : 'Add Goal'}</span>
          </button>
        </div>
      </div>

      {/* Add/Edit Goal Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              {editingGoal ? 'Edit Goal' : 'Add New Strategic Goal'}
            </h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goal Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter goal title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Describe the goal in detail"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-1" />
                    Achieve By Date
                  </label>
                  <input
                    type="date"
                    name="achieve_by"
                    value={formData.achieve_by}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiPercent} className="w-4 h-4 inline mr-1" />
                    Achieved
                  </label>
                  <input
                    type="number"
                    name="percentage_achieved"
                    value={formData.percentage_achieved}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0-100"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={cancelForm}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{editingGoal ? 'Update Goal' : 'Add Goal'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Goals */}
      <div className="space-y-4">
        {activeGoals.length > 0 ? (
          activeGoals.map((goal) => (
            <motion.div
              key={goal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 border rounded-lg ${
                goal.percentage_achieved >= 100 
                  ? 'border-green-200 bg-green-50' 
                  : isOverdue(goal.achieve_by) 
                    ? 'border-red-200 bg-red-50' 
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Completion Checkmark */}
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1 ${
                    goal.percentage_achieved >= 100 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-400'
                  }`}>
                    {goal.percentage_achieved >= 100 && <SafeIcon icon={FiCheck} className="w-4 h-4" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-semibold ${
                        goal.percentage_achieved >= 100 ? 'text-green-800' : 'text-gray-900'
                      }`}>
                        {goal.title}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditClick(goal)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit goal"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleArchiveClick(goal)}
                          className="text-amber-600 hover:text-amber-800 p-1"
                          title="Archive goal"
                        >
                          <SafeIcon icon={FiArchive} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {goal.description && (
                      <p className="text-gray-600 text-sm mb-3">{goal.description}</p>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                          <span className={`${
                            isOverdue(goal.achieve_by) ? 'text-red-600 font-medium' : 'text-gray-600'
                          }`}>
                            By {goal.achieve_by ? format(new Date(goal.achieve_by), 'MMM d, yyyy') : 'No date set'}
                            {isOverdue(goal.achieve_by) && ' (Overdue)'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiPercent} className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{goal.percentage_achieved || 0}% achieved</span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(goal.percentage_achieved || 0)}`}
                            style={{ width: `${Math.min(100, goal.percentage_achieved || 0)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600 min-w-[3rem]">
                          {goal.percentage_achieved || 0}%
                        </span>
                      </div>
                    </div>

                    {goal.coach_name && (
                      <div className="mt-2 text-xs text-gray-500">
                        Added by {goal.coach_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiTarget} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No strategic goals set yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-2 text-primary-600 hover:text-primary-700 font-medium"
            >
              Add the first goal
            </button>
          </div>
        )}
      </div>

      {/* Archived Goals */}
      <AnimatePresence>
        {showArchived && archivedGoals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 pt-6 border-t border-gray-200"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Archived Goals</h4>
            <div className="space-y-3">
              {archivedGoals.map((goal) => (
                <div
                  key={goal.id}
                  className="p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-75"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-700">{goal.title}</h5>
                      {goal.description && (
                        <p className="text-gray-600 text-sm mt-1">{goal.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
                        <span>By {goal.achieve_by ? format(new Date(goal.achieve_by), 'MMM d, yyyy') : 'No date'}</span>
                        <span>Achieved: {goal.percentage_achieved || 0}%</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRestoreClick(goal)}
                      className="text-green-600 hover:text-green-800 p-1"
                      title="Restore goal"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Archive Confirmation Modal */}
      <AnimatePresence>
        {archiveModalOpen && goalToAction && (
          <DeleteConfirmModal
            title="Archive Goal"
            message={`Are you sure you want to archive "${goalToAction.title}"? This will hide it from the active goals list, but it can be restored later.`}
            confirmText="Archive"
            confirmIcon={FiArchive}
            confirmColor="bg-amber-600 hover:bg-amber-700"
            onConfirm={handleConfirmArchive}
            onCancel={() => {
              setArchiveModalOpen(false);
              setGoalToAction(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {restoreModalOpen && goalToAction && (
          <DeleteConfirmModal
            title="Restore Goal"
            message={`Are you sure you want to restore "${goalToAction.title}" to the active goals list?`}
            confirmText="Restore"
            confirmIcon={FiRefreshCw}
            confirmColor="bg-green-600 hover:bg-green-700"
            onConfirm={handleConfirmRestore}
            onCancel={() => {
              setRestoreModalOpen(false);
              setGoalToAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StrategicGoals;